import type { CallRecord, Extension } from '@/types';
import { callingExtension, dialledNumber } from '@/lib/callCosts';
import { phonesLikelyMatch } from '@/lib/failoverCorrelation';

/**
 * One record per call from Yeastar's CDR, where a call is every row (leg) that shares a uid.
 * The dashboard's numbers all come from here, so the cards, the per-extension table and the
 * missed calls list cannot disagree.
 *
 * An inbound call is:
 * - answered when an extension picked up one of its legs (an IVR or prompt "answering" is the
 *   PBX, not a person), or it was forwarded to an outside number that picked up;
 * - voicemail when nobody answered and it reached a voicemail box;
 * - missed when nobody answered, there is no voicemail, and it rang a queue, ring group or
 *   extension;
 * - ivrAbandoned when it never got past the IVR or a prompt. Those shorter than
 *   SHORT_CALL_SECONDS are flagged shortSpam and left out of the headline numbers.
 * Outbound and internal calls are never missed.
 */

export type CallDirection = 'inbound' | 'outbound' | 'internal';
export type CallOutcome = 'answered' | 'voicemail' | 'missed' | 'ivrAbandoned';
export type LegKind = 'extension' | 'queue' | 'ringGroup' | 'ivr' | 'prompt' | 'voicemail' | 'external' | 'other';

export const SHORT_CALL_SECONDS = 5;

export interface CallLeg {
  record: CallRecord;
  kind: LegKind;
  /** "CQ-Reception", "Sarah Mahoro", "IVR-OfficeHours". */
  name: string;
  /** The extension, queue or IVR number; the dialled number for an outside leg. */
  number: string;
  atMs: number;
  answered: boolean;
  /** How long it rang before being answered or given up on. */
  ringSeconds: number;
  talkSeconds: number;
}

export interface RouteStep {
  kind: LegKind;
  label: string;
  /** Ring time for a queue, ring group or extension that did not answer. */
  seconds?: number;
}

export type CallFollowUp =
  | { kind: 'calledBack'; atMs: number; by: string; answered: boolean }
  | { kind: 'rangAgain'; atMs: number; by: string };

export interface ClassifiedCall {
  uid: string;
  direction: CallDirection;
  /** Inbound calls only. */
  outcome?: CallOutcome;
  /** An IVR hang-up shorter than SHORT_CALL_SECONDS. */
  shortSpam: boolean;
  atMs: number;
  /** The first leg's time as the PBX wrote it. */
  time: string;
  /** Who called: "Name<number>" as the CDR writes it. */
  caller: string;
  callerNumber: string;
  legs: CallLeg[];
  /** Seconds from the first leg starting to the last one ending. */
  durationSeconds: number;
  /** Total ring time at queues, ring groups and extensions. */
  waitSeconds: number;
  /** Talk time with a person (extension legs, or the outside party on an outbound call). */
  talkSeconds: number;
  route: RouteStep[];
  /** Extension that answered (inbound) or placed the call (outbound). */
  extension?: string;
  /** Extensions that rang on an inbound call; for each, whether it answered. */
  extensionsRang: Map<string, boolean>;
  /** The row to open in the call detail view. */
  record: CallRecord;
  /** For a missed or voicemail call: a later call back or answered call from the same number that day. */
  followUp?: CallFollowUp;
}

export interface CallsSummary {
  /** Inbound calls, short IVR hang-ups left out. */
  received: number;
  made: number;
  /** received + made. */
  total: number;
  answered: number;
  missed: number;
  voicemail: number;
  /** IVR hang-ups, short ones left out. */
  ivrAbandoned: number;
  shortSpam: number;
  internal: number;
  /** Missed or voicemail calls that were called back, or the caller got through later that day. */
  followedUp: number;
}

export interface ExtensionCallStats {
  ext_num: string;
  ext_name: string;
  /** Inbound calls that rang this extension. */
  received: number;
  /** Outbound calls it placed. */
  made: number;
  total: number;
  /** Calls that rang here and were not picked up here; someone else may have answered. */
  unansweredRings: number;
  /** Unanswered rings where the whole call ended up missed or in voicemail. */
  trulyMissed: number;
  /** Talk seconds on its legs. */
  duration: number;
}

const PREFIXES: Array<[RegExp, LegKind]> = [
  [/^Queue\s/i, 'queue'],
  [/^Ring\s?Group\s/i, 'ringGroup'],
  [/^IVR\s/i, 'ivr'],
  [/^Voicemail\b/i, 'voicemail'],
  [/^Play\s?Prompt\b/i, 'prompt'],
];

function bracketed(value: string): { name: string; number: string } {
  const match = value.match(/^(.*?)\s*<([^>]*)>\s*$/);
  return match ? { name: match[1].trim(), number: match[2].trim() } : { name: value.trim(), number: '' };
}

export function recordMs(record: CallRecord): number {
  const ts = Number(record.timestamp);
  if (Number.isFinite(ts) && ts > 0) return ts > 1_000_000_000_000 ? ts : ts * 1000;
  const parsed = Date.parse(String(record.time || '').replace(/-/g, '/'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

const seconds = (value: unknown) => Math.max(0, Math.round(Number(value) || 0));

function isAnswered(record: CallRecord) {
  return String(record.disposition || '').trim().toUpperCase() === 'ANSWERED';
}

/** What a CDR row's destination is. `extensions` decides what counts as an extension. */
export function legKind(record: CallRecord, extensions: Set<string>): LegKind {
  const to = String(record.call_to || '').trim();
  for (const [pattern, kind] of PREFIXES) if (pattern.test(to)) return kind;
  if (/voicemail/i.test(String(record.disposition || ''))) return 'voicemail';
  if (record.call_type === 'Outbound') return 'external';
  const number = String(record.call_to_number || bracketed(to).number || to).trim();
  if (extensions.size > 0) return extensions.has(number) ? 'extension' : 'other';
  // No extension list: "Name<101>" with a short number is an extension
  return /^\d{1,7}$/.test(number) ? 'extension' : 'other';
}

function toLeg(record: CallRecord, extensions: Set<string>): CallLeg {
  const kind = legKind(record, extensions);
  const to = String(record.call_to || '').trim();
  const stripped = to.replace(/^(Queue|Ring\s?Group|IVR|Voicemail)\s+/i, '');
  const parts = bracketed(stripped);
  const number =
    kind === 'external' ? dialledNumber(record) : String(parts.number || record.call_to_number || '').trim();
  const answered = isAnswered(record);
  const duration = seconds(record.duration);
  const talk = seconds(record.talk_duration);
  const ring =
    record.ring_duration !== undefined && record.ring_duration !== null
      ? seconds(record.ring_duration)
      : answered
        ? Math.max(0, duration - talk)
        : duration;
  return {
    record,
    kind,
    name: kind === 'prompt' ? 'Prompt' : parts.name || number || to,
    number,
    atMs: recordMs(record),
    answered,
    ringSeconds: ring,
    talkSeconds: answered ? talk || Math.max(0, duration - ring) : 0,
  };
}

/** Oldest first; legs logged in the same second keep the PBX's own order (new_id). */
function legOrder(a: CallLeg, b: CallLeg) {
  return a.atMs - b.atMs || String(a.record.new_id || '').localeCompare(String(b.record.new_id || ''));
}

function routeOf(legs: CallLeg[]): RouteStep[] {
  const steps: RouteStep[] = [];
  for (const leg of legs) {
    const ringing = leg.kind === 'queue' || leg.kind === 'ringGroup' || leg.kind === 'extension';
    if (leg.kind === 'voicemail') steps.push({ kind: leg.kind, label: 'Voicemail' });
    else if (leg.kind === 'external') steps.push({ kind: leg.kind, label: leg.number || leg.name });
    else steps.push({ kind: leg.kind, label: leg.name, seconds: ringing && !leg.answered ? leg.ringSeconds : undefined });
  }
  return steps;
}

function directionOf(legs: CallLeg[]): CallDirection {
  if (legs.some((leg) => leg.record.call_type === 'Inbound')) return 'inbound';
  if (legs.some((leg) => leg.record.call_type === 'Outbound')) return 'outbound';
  return 'internal';
}

function callerNumberOf(record: CallRecord) {
  const raw = String(record.call_from_number || bracketed(String(record.call_from || '')).number || record.call_from || '');
  return raw.replace(/[^\d+]/g, '');
}

function buildCall(uid: string, records: CallRecord[], extensions: Set<string>): ClassifiedCall {
  const legs = records.map((record) => toLeg(record, extensions)).sort(legOrder);
  const first = legs[0];
  const direction = directionOf(legs);
  const startMs = first.atMs;
  const endMs = Math.max(...legs.map((leg) => leg.atMs + seconds(leg.record.duration) * 1000));
  const durationSeconds = Math.max(0, Math.round((endMs - startMs) / 1000));

  const call: ClassifiedCall = {
    uid,
    direction,
    shortSpam: false,
    atMs: startMs,
    time: String(first.record.time || ''),
    caller: String(first.record.call_from || ''),
    callerNumber: callerNumberOf(first.record),
    legs,
    durationSeconds,
    waitSeconds: 0,
    talkSeconds: 0,
    route: routeOf(legs),
    extensionsRang: new Map(),
    record: first.record,
  };

  if (direction === 'outbound') {
    const out = legs.find((leg) => leg.record.call_type === 'Outbound') ?? first;
    call.record = out.record;
    call.extension = callingExtension(out.record) ?? undefined;
    call.talkSeconds = legs.reduce((sum, leg) => sum + leg.talkSeconds, 0);
    return call;
  }
  if (direction === 'internal') return call;

  const inbound = legs.filter((leg) => leg.record.call_type === 'Inbound');
  const ringing = inbound.filter((leg) => leg.kind === 'queue' || leg.kind === 'ringGroup' || leg.kind === 'extension');
  for (const leg of inbound) {
    if (leg.kind !== 'extension') continue;
    call.extensionsRang.set(leg.number, (call.extensionsRang.get(leg.number) ?? false) || leg.answered);
  }
  const answeredExt = inbound.find((leg) => leg.kind === 'extension' && leg.answered);
  // Forwarded out (follow me, a queue's failover to a mobile) and picked up there
  const answeredOutside = legs.find((leg) => leg.kind === 'external' && leg.answered && leg.talkSeconds > 0);
  const voicemail = legs.find((leg) => leg.kind === 'voicemail');

  call.waitSeconds = ringing.reduce((sum, leg) => sum + leg.ringSeconds, 0);
  call.talkSeconds = inbound.filter((leg) => leg.kind === 'extension').reduce((sum, leg) => sum + leg.talkSeconds, 0);

  if (answeredExt || answeredOutside) {
    call.outcome = 'answered';
    call.extension = answeredExt?.number;
    call.record = (answeredExt ?? answeredOutside)!.record;
  } else if (voicemail) {
    call.outcome = 'voicemail';
    call.record = voicemail.record;
  } else if (ringing.length > 0) {
    call.outcome = 'missed';
    call.record = ringing[ringing.length - 1].record;
  } else {
    call.outcome = 'ivrAbandoned';
    call.shortSpam = durationSeconds < SHORT_CALL_SECONDS;
  }
  return call;
}

function sameDay(a: number, b: number) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

/** A later call back, or the caller ringing again and getting through, on the same day. */
function followUpOf(missed: ClassifiedCall, calls: ClassifiedCall[], label: (ext: string) => string): CallFollowUp | undefined {
  if (missed.callerNumber.replace(/\D/g, '').length < 6) return undefined;
  for (const call of calls) {
    if (call.atMs <= missed.atMs || !sameDay(call.atMs, missed.atMs)) continue;
    if (call.direction === 'outbound' && phonesLikelyMatch(dialledNumber(call.record), missed.callerNumber)) {
      return {
        kind: 'calledBack',
        atMs: call.atMs,
        by: call.extension ? label(call.extension) : String(call.record.call_from || ''),
        answered: call.talkSeconds > 0 || isAnswered(call.record),
      };
    }
    if (call.direction === 'inbound' && call.outcome === 'answered' && phonesLikelyMatch(call.callerNumber, missed.callerNumber)) {
      return { kind: 'rangAgain', atMs: call.atMs, by: call.extension ? label(call.extension) : 'someone' };
    }
  }
  return undefined;
}

/** Groups CDR rows into calls by uid and sorts each into answered, voicemail, missed or IVR hang-up. */
export function classifyCalls(
  records: CallRecord[],
  options: { extensions?: Array<Pick<Extension, 'number'> & Partial<Pick<Extension, 'display_name' | 'username'>>> } = {}
): { calls: ClassifiedCall[]; summary: CallsSummary } {
  const extensionList = options.extensions ?? [];
  const extensions = new Set(extensionList.map((ext) => String(ext.number)));
  const names = new Map(extensionList.map((ext) => [String(ext.number), ext.display_name || ext.username || '']));
  const label = (ext: string) => names.get(ext) || ext;

  const byUid = new Map<string, CallRecord[]>();
  records.forEach((record, index) => {
    // A row without a uid is a call of its own
    const uid = String(record.uid || `row-${record.new_id || record.id || index}`);
    const list = byUid.get(uid);
    if (list) list.push(record);
    else byUid.set(uid, [record]);
  });

  const calls = Array.from(byUid, ([uid, rows]) => buildCall(uid, rows, extensions)).sort((a, b) => a.atMs - b.atMs);
  for (const call of calls) {
    if (call.outcome === 'missed' || call.outcome === 'voicemail') call.followUp = followUpOf(call, calls, label);
  }

  const summary: CallsSummary = {
    received: 0,
    made: 0,
    total: 0,
    answered: 0,
    missed: 0,
    voicemail: 0,
    ivrAbandoned: 0,
    shortSpam: 0,
    internal: 0,
    followedUp: 0,
  };
  for (const call of calls) {
    if (call.direction === 'internal') summary.internal += 1;
    else if (call.direction === 'outbound') summary.made += 1;
    else if (call.shortSpam) summary.shortSpam += 1;
    else {
      summary.received += 1;
      if (call.outcome === 'answered') summary.answered += 1;
      else if (call.outcome === 'voicemail') summary.voicemail += 1;
      else if (call.outcome === 'missed') summary.missed += 1;
      else summary.ivrAbandoned += 1;
      if (call.followUp) summary.followedUp += 1;
    }
  }
  summary.total = summary.received + summary.made;
  return { calls, summary };
}

/** Missed and voicemail calls, oldest first: the "Missed calls" list. */
export function missedCalls(calls: ClassifiedCall[]): ClassifiedCall[] {
  return calls.filter((call) => call.outcome === 'missed' || call.outcome === 'voicemail');
}

/**
 * Per-extension counts from classified calls. Every extension in `extensions` gets a row, plus
 * any other extension number seen on a call. Each call counts once per extension.
 */
export function extensionCallStats(
  calls: ClassifiedCall[],
  extensions: Array<Pick<Extension, 'number'> & Partial<Pick<Extension, 'display_name' | 'username'>>>
): ExtensionCallStats[] {
  const rows = new Map<string, ExtensionCallStats>();
  const ensure = (number: string, name?: string) => {
    let row = rows.get(number);
    if (!row) {
      row = { ext_num: number, ext_name: name || number, received: 0, made: 0, total: 0, unansweredRings: 0, trulyMissed: 0, duration: 0 };
      rows.set(number, row);
    }
    return row;
  };
  for (const ext of extensions) ensure(String(ext.number), ext.display_name || ext.username);

  for (const call of calls) {
    if (call.direction === 'outbound') {
      if (!call.extension) continue;
      const row = ensure(call.extension);
      row.made += 1;
      row.duration += call.talkSeconds;
    } else if (call.direction === 'inbound') {
      for (const [number, answered] of call.extensionsRang) {
        const row = ensure(number, call.legs.find((leg) => leg.kind === 'extension' && leg.number === number)?.name);
        row.received += 1;
        if (!answered) {
          row.unansweredRings += 1;
          if (call.outcome === 'missed' || call.outcome === 'voicemail') row.trulyMissed += 1;
        }
      }
      for (const leg of call.legs) {
        if (leg.kind === 'extension' && leg.record.call_type === 'Inbound') ensure(leg.number).duration += leg.talkSeconds;
      }
    }
  }
  for (const row of rows.values()) row.total = row.received + row.made;
  return Array.from(rows.values()).sort((a, b) => a.ext_num.localeCompare(b.ext_num, undefined, { numeric: true }));
}

/** "IVR-OfficeHours → CQ-Reception (25s) → Voicemail". */
export function routeText(route: RouteStep[]): string {
  return route.map((step) => (step.seconds !== undefined ? `${step.label} (${step.seconds}s)` : step.label)).join(' → ');
}
